import { modals, useToast, Box } from '@cad-arc';
import {
    AddAssignedUnitsRequest,
    AddAssignedUnitsResponse,
    AssigneventResource,
} from '@mark43/cad-api';
import { useCallback, useRef } from 'react';
import { useApiClient, createApiErrorHandler } from '@m43/core';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { showAssignAndHoldConfirmationModal } from '../common/show-assign-and-hold-confirmation-modal';

export function useAddAssignedUnitsApi(): (
    addAssignedUnitsRequest: AddAssignedUnitsRequest,
    showSuccessMessage?: boolean
) => Promise<AddAssignedUnitsResponse | undefined> {
    const { automaticallyHoldEventsForUnits } = useEnabledFeatures();
    const apiClient = useApiClient();

    const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

    const toast = useToast();

    const submit = useCallback(
        (addAssignedUnitsRequest: AddAssignedUnitsRequest, showSuccessMessage?: boolean) => {
            const errorMessage = errorOccurredWhile(
                addAssignedUnitsRequest.unitIds.length > 1 ? 'assigning units' : 'assigning unit'
            );

            return apiClient
                .request<AssigneventResource.AddAssignedUnitsWithConfirmation>({
                    method: 'POST',
                    url: `assign/event/add_units/`,
                    data: addAssignedUnitsRequest,
                })
                .then((resp) => {
                    if (resp.data.data?.confirmationMessage) {
                        const body = (
                            <Box whiteSpace="pre-line">{resp.data.data.confirmationMessage}</Box>
                        );
                        if (resp.data.data.showAssignAndHoldConfirmationOption) {
                            showAssignAndHoldConfirmationModal({
                                title: 'Assign Unit',
                                body,
                                onAssignAndHold: () =>
                                    submit(
                                        {
                                            ...addAssignedUnitsRequest,
                                            confirmationAcknowledged: true,
                                            assignAndHold: true,
                                        },
                                        showSuccessMessage
                                    ).catch(
                                        createApiErrorHandler(() => {
                                            toast({
                                                status: 'error',
                                                description: errorMessage,
                                            });
                                        })
                                    ),
                                onAssignOnly: () =>
                                    submit(
                                        {
                                            ...addAssignedUnitsRequest,
                                            confirmationAcknowledged: true,
                                            assignAndHold: false,
                                        },
                                        showSuccessMessage
                                    ).catch(
                                        createApiErrorHandler(() => {
                                            toast({
                                                status: 'error',
                                                description: errorMessage,
                                            });
                                        })
                                    ),
                                cancelButtonRef,
                            });
                        } else {
                            modals.confirm({
                                title: 'Assign Unit',
                                body,
                                onConfirm: () => {
                                    submit(
                                        {
                                            ...addAssignedUnitsRequest,
                                            confirmationAcknowledged: true,
                                            assignAndHold: automaticallyHoldEventsForUnits,
                                        },
                                        showSuccessMessage
                                    ).catch(
                                        createApiErrorHandler(() => {
                                            toast({
                                                status: 'error',
                                                description: errorMessage,
                                            });
                                        })
                                    );
                                },
                            });
                        }
                    } else if (resp.data.success && showSuccessMessage) {
                        toast({
                            status: 'default',
                            description: resp.data.data?.successMessage,
                        });
                    }
                    return resp.data.data;
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorMessage,
                        });
                    })
                );
        },
        [apiClient, automaticallyHoldEventsForUnits, toast]
    );
    return submit;
}
